import { useEffect, useRef, useState } from "react";
import { masterApi, apiError, formatBRL, formatL, PRODUTOS_LABEL } from "@/lib/api";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ExcelBtn } from "@/lib/excelDownload";
import { modal } from "@/lib/modal";
import { inicioDiaBR, fimDiaBR, atalhoPeriodoBR } from "@/lib/periodoBR";
import { comPagina, normalizaPaginado } from "@/lib/paginacao";
import Pagination from "@/components/crud/Pagination";
import { Receipt, AlertTriangle, Zap } from "lucide-react";

/**
 * Item 6 (19/10) — antifraude: frentista com ticket alto.
 * Compara cada frentista com os colegas da MESMA unidade, produto e categoria (sem ele mesmo),
 * no período escolhido (dia de Brasília), e com o período anterior equivalente. Só leitura.
 */

const ATALHOS = [["hoje", "Hoje"], ["ontem", "Ontem"], ["7d", "7 dias"], ["30d", "30 dias"], ["mes", "Este mês"]];
const PRODUTOS_FILTRO = Object.entries(PRODUTOS_LABEL || {}).filter(([k]) => k !== "troca_oleo");
const MOTIVO_LBL = {
  acima_p90: "Acima do P90 da unidade",
  p90: "Acima do P90 da unidade",
  acima_2x_cliente: "Mais de 2x a mediana do cliente",
  acima_mediana_cliente: "Mais de 2x a mediana do cliente",
  cliente_2x: "Mais de 2x a mediana do cliente",
};

/** Texto do filtro -> número > 0 (inteiro se pedido); vazio/inválido usa o padrão. */
export function numeroPositivo(txt, padrao, inteiro = false) {
  const v = parseFloat(String(txt ?? "").replace(",", "."));
  if (!Number.isFinite(v) || v <= 0) return padrao;
  return inteiro ? Math.max(1, Math.round(v)) : v;
}

const num = (v, casas = 1) => (v == null || !Number.isFinite(Number(v)) ? "—"
  : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const razao = (v) => (v == null || !Number.isFinite(Number(v)) ? "—" : `${num(v, 2)}x`);
// O backend manda o motivo pronto ("Valor acima do P90 da unidade (R$ 1.234,56); Litros acima de 2x
// a mediana do cliente (12,50 L)"): separa só por ";" (a vírgula faz parte dos valores).
const motivoTxt = (m) => {
  if (!m) return "—";
  if (Array.isArray(m)) return m.map(motivoTxt).join(" · ");
  return String(m).split(";").map((p) => p.trim()).filter(Boolean).map((p) => MOTIVO_LBL[p] || p).join(" · ");
};

/**
 * Razão que define a cor do alerta, como o backend (services/antifraude_ticket.nivel): a MAIOR entre
 * ticket x colegas, litros x colegas e ticket atual x período anterior (esta só quando o período
 * anterior tem o mínimo de atendimentos). O selo mostra essa razão, com o que a disparou.
 */
export function motivoNivel(r, minAtendimentos) {
  const cands = [["ticket", r?.razao_ticket], ["litros", r?.razao_litros]];
  const ant = r?.periodo_anterior;
  const min = Number(minAtendimentos) || 1;
  if (ant && Number(ant.atendimentos) >= min) cands.push(["vs. anterior", ant.variacao_ticket]);
  let melhor = null;
  for (const [tipo, v] of cands) {
    const n = v == null || v === "" ? NaN : Number(v);
    if (Number.isFinite(n) && (!melhor || n > melhor.razao)) melhor = { tipo, razao: n };
  }
  return melhor;
}

/** Selo no padrão de "Uso de desconto por vendedor" (MasterRelatorios). */
export function SeloNivel({ nivel, children, testid }) {
  const cls = nivel === "vermelho" ? "bg-rose-100 text-rose-800"
    : nivel === "amarelo" ? "bg-amber-100 text-amber-800"
    : "bg-emerald-50 text-emerald-700";
  return (
    <span data-testid={testid} data-nivel={nivel || "ok"}
          className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${cls}`}>
      {children}
      {nivel === "vermelho" && <AlertTriangle size={11} className="inline-block ml-1 -mt-0.5" />}
      {nivel === "amarelo" && <Zap size={11} className="inline-block ml-1 -mt-0.5" />}
    </span>
  );
}

export default function TicketAltoFrentista() {
  const { unitId } = useUnit();
  const inicial = atalhoPeriodoBR("hoje"); // padrão do dono: período abre em Hoje
  const [inicio, setInicio] = useState(inicial.inicio);
  const [fim, setFim] = useState(inicial.fim);
  const [produto, setProduto] = useState("");
  const [categoria, setCategoria] = useState("");
  // Texto digitado (o campo numérico fica livre para "1," / "1.") e valor usado na consulta.
  const [minAtdTxt, setMinAtdTxt] = useState("10");
  const [fatorATxt, setFatorATxt] = useState("1.3");
  const [fatorVTxt, setFatorVTxt] = useState("1.6");
  const minAtd = numeroPositivo(minAtdTxt, 10, true);
  const fatorA = numeroPositivo(fatorATxt, 1.3);
  const fatorV = numeroPositivo(fatorVTxt, 1.6);
  const [data, setData] = useState(null);
  const [detalhe, setDetalhe] = useState(null); // {item, page, items, total, total_pages, carregando}

  const ini = inicioDiaBR(inicio);
  const fi = fimDiaBR(fim);
  const periodoValido = !!(ini && fi && inicio <= fim);
  const fatoresValidos = fatorV >= fatorA;
  const filtrosValidos = periodoValido && fatoresValidos;

  const filtrosBase = (unit) => {
    const p = new URLSearchParams();
    if (ini) p.set("inicio", ini);
    if (fi) p.set("fim", fi);
    if (unit && unit !== "all") p.set("unit_id", unit);
    if (produto) p.set("produto", produto);
    if (categoria) p.set("categoria", categoria);
    return p;
  };
  const qsRelatorio = () => {
    const p = filtrosBase(unitId);
    p.set("min_atendimentos", String(minAtd));
    p.set("fator_amarelo", String(fatorA));
    p.set("fator_vermelho", String(fatorV));
    return p.toString();
  };

  // Troca rápida de filtros: só a resposta da consulta mais recente entra na tela.
  const seq = useRef(0);
  const load = async () => {
    const minha = ++seq.current;
    if (!filtrosValidos) { setData(null); return; }
    try {
      const r = await masterApi.get(`/master/analises/ticket-alto-frentista?${qsRelatorio()}`);
      if (minha === seq.current) setData(r.data);
    } catch (e) { if (minha === seq.current) modal.error(apiError(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [unitId, inicio, fim, produto, categoria, minAtd, fatorA, fatorV]);

  const carregarDetalhe = async (item, page = 1) => {
    setDetalhe((d) => ({ ...(d && d.item?.frentista_id === item.frentista_id ? d : {}), item, page, carregando: true }));
    try {
      const unit = item.unit_id || unitId;
      const url = comPagina(`/master/analises/ticket-alto-frentista/${encodeURIComponent(item.frentista_id)}/atendimentos?${filtrosBase(unit).toString()}`, page);
      const r = await masterApi.get(url);
      const pg = normalizaPaginado(r.data, page);
      setDetalhe({ item, page: pg.page, items: pg.items, total: pg.total, total_pages: pg.total_pages, carregando: false });
    } catch (e) {
      setDetalhe((d) => (d ? { ...d, carregando: false } : d));
      modal.error(apiError(e));
    }
  };

  const itens = data?.itens || data?.items || [];
  // Mínimo usado na consulta (o backend devolve em parametros) para saber se o período anterior conta.
  const minUsado = Number(data?.parametros?.min_atendimentos) || minAtd;
  const seloTxt = (r) => {
    const m = motivoNivel(r, minUsado);
    return m ? `${m.tipo} ${razao(m.razao)}` : "—";
  };
  const excelUrl = `/master/analises/ticket-alto-frentista/excel?${qsRelatorio()}`;

  return (
    <div className="card-soft rounded-xl p-4 space-y-3" data-testid="ticket-alto">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 mr-auto">
          <Receipt size={15} className="text-rose-600" />
          <h3 className="font-serif-display text-base">Ticket alto por frentista</h3>
        </div>
        <ExcelBtn testid="ticket-alto-excel" url={excelUrl} filename="ticket_alto_frentista.xlsx" />
      </div>

      <div className="flex flex-wrap gap-3 items-end" data-testid="ticket-alto-filtros">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Início</label>
          <input type="date" data-testid="ta-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Fim</label>
          <input type="date" data-testid="ta-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                 className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block" />
        </div>
        <div className="flex flex-wrap gap-1">
          {ATALHOS.map(([k, lbl]) => (
            <button key={k} data-testid={`ta-atalho-${k}`}
                    onClick={() => { const a = atalhoPeriodoBR(k); setInicio(a.inicio); setFim(a.fim); }}
                    className="btn-ghost h-9 px-2.5 text-[11px] font-medium">{lbl}</button>
          ))}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Produto</label>
          <select data-testid="ta-produto" value={produto} onChange={(e) => setProduto(e.target.value)}
                  className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block">
            <option value="">Todos</option>
            {PRODUTOS_FILTRO.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Categoria</label>
          <select data-testid="ta-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}
                  className="mt-1 h-9 rounded-md bg-white border border-neutral-200 px-2 text-sm block">
            <option value="">Todas</option>
            <option value="normal">Normal</option>
            <option value="app_driver">Motorista de App</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <StepperNum label="mín. atend." value={minAtdTxt} onChange={setMinAtdTxt} testid="ta-min" />
          <StepperNum label="amarelo" value={fatorATxt} step={0.1} onChange={setFatorATxt} testid="ta-fator-amarelo" />
          <StepperNum label="vermelho" value={fatorVTxt} step={0.1} onChange={setFatorVTxt} testid="ta-fator-vermelho" />
        </div>
      </div>

      <p className="text-[11px] text-neutral-500">
        Ticket médio e litros médios de cada frentista contra a média dos <b>colegas da mesma unidade, produto e categoria</b> (sem
        ele mesmo), no período escolhido, e contra o período anterior de mesmo tamanho. Só vendas finalizadas, sem troca de óleo.
        Alerta a partir de {num(fatorA, 1)}x (amarelo) e {num(fatorV, 1)}x (vermelho), com pelo menos {minAtd} atendimentos.
        Clique no frentista para ver os atendimentos acima do P90 da unidade ou acima de 2x a mediana do próprio cliente.
      </p>

      {!periodoValido && (
        <div className="text-[12px] text-rose-700" data-testid="ta-periodo-invalido">Informe um período válido (início antes do fim).</div>
      )}
      {periodoValido && !fatoresValidos && (
        <div className="text-[12px] text-rose-700" data-testid="ta-fatores-invalidos">O fator vermelho deve ser maior ou igual ao amarelo.</div>
      )}

      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead className="text-[10px] uppercase tracking-widest">Frentista</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-center">Atend.</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Ticket médio</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Litros médios</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-center">% acima P90</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Desc. médio</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-center">Acima 2x cliente</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Período anterior</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-center">Alerta</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {itens.map((r) => {
              const ant = r.periodo_anterior;
              return (
                <TableRow key={`${r.frentista_id}-${r.unit_id || ""}`} data-testid={`ta-row-${r.frentista_id}`}>
                  <TableCell>
                    <button data-testid={`ta-frentista-${r.frentista_id}`} onClick={() => carregarDetalhe(r, 1)}
                            title="Ver atendimentos suspeitos"
                            className="font-medium text-left text-[color:var(--brand-blue)] hover:underline">
                      {r.frentista_nome || r.frentista_id}
                    </button>
                    <div className="text-[10.5px] text-neutral-500">{r.unit_nome || "—"}</div>
                  </TableCell>
                  <TableCell className="text-center">{r.atendimentos}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-semibold">{formatBRL(r.ticket_medio)}</div>
                    <div className="text-[10.5px] text-neutral-500">colegas {formatBRL(r.ticket_medio_colegas)} · {razao(r.razao_ticket)}</div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div>{num(r.litros_medio, 1)} L</div>
                    <div className="text-[10.5px] text-neutral-500">colegas {num(r.litros_medio_colegas, 1)} L · {razao(r.razao_litros)}</div>
                  </TableCell>
                  <TableCell className="text-center">{r.pct_acima_p90 == null ? "—" : `${num(r.pct_acima_p90, 1)}%`}</TableCell>
                  <TableCell className="whitespace-nowrap">{formatBRL(r.desconto_medio)}</TableCell>
                  <TableCell className="text-center">{r.atend_litros_acima_hist_cliente ?? "—"}</TableCell>
                  <TableCell className="text-[11px] whitespace-nowrap" data-testid={`ta-anterior-${r.frentista_id}`}>
                    {ant ? (
                      <>
                        <div>{ant.atendimentos} atend. · {formatBRL(ant.ticket_medio)}</div>
                        <div className="text-neutral-500">
                          razão {razao(ant.razao_ticket)}
                          {ant.variacao_ticket != null && <> · atual x anterior <b className="text-neutral-700">{razao(ant.variacao_ticket)}</b></>}
                        </div>
                      </>
                    ) : <span className="text-neutral-400">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <SeloNivel nivel={r.nivel} testid={`ta-nivel-${r.frentista_id}`}>{seloTxt(r)}</SeloNivel>
                  </TableCell>
                </TableRow>
              );
            })}
            {data && itens.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-5">Nenhum frentista com o mínimo de atendimentos no período.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="text-[10px] text-neutral-500 px-1">
        <span className="inline-block w-2 h-2 rounded-full bg-rose-400 mr-1"></span> ≥{num(fatorV, 1)}x ·
        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1 ml-2"></span> ≥{num(fatorA, 1)}x
        {" "}(colegas ou período anterior; o selo mostra a razão que disparou: ticket, litros ou vs. anterior) ·
        Considera apenas frentistas com ≥{minAtd} atendimentos no período.
      </div>

      <Dialog open={!!detalhe} onOpenChange={(v) => !v && setDetalhe(null)}>
        <DialogContent className="max-w-4xl rounded-2xl border-none p-0">
          {detalhe && (
            <div className="bg-white p-5 rounded-2xl space-y-3" data-testid="ta-detalhe">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Atendimentos suspeitos</div>
                <h2 className="font-serif-display text-xl leading-tight">{detalhe.item.frentista_nome || detalhe.item.frentista_id}</h2>
                <div className="text-[11px] text-neutral-500">
                  {detalhe.item.unit_nome ? `${detalhe.item.unit_nome} · ` : ""}{inicio.split("-").reverse().join("/")} a {fim.split("-").reverse().join("/")}
                  {" "}· acima do P90 da unidade ou acima de 2x a mediana do próprio cliente
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-neutral-50">
                    <TableHead className="text-[10px] uppercase tracking-widest">Data</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Produto</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Litros</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Valor bruto</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Desconto</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest">Motivo</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {(detalhe.items || []).map((a) => (
                      <TableRow key={a.id} data-testid={`ta-atd-${a.id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{a.criado_em ? new Date(a.criado_em).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—"}</TableCell>
                        <TableCell>
                          <div className="font-medium leading-tight">{a.cliente_nome || "—"}</div>
                          <div className="text-[10.5px] text-neutral-500 font-mono uppercase">{a.placa || "—"}</div>
                        </TableCell>
                        <TableCell className="text-xs">{PRODUTOS_LABEL?.[a.produto] || a.produto || "—"}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatL(a.litros)}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatBRL(a.valor_bruto)}</TableCell>
                        <TableCell className="whitespace-nowrap text-rose-700">{formatBRL(a.desconto_total_reais)}</TableCell>
                        <TableCell className="text-[11px]">{motivoTxt(a.motivo)}</TableCell>
                      </TableRow>
                    ))}
                    {!detalhe.carregando && (detalhe.items || []).length === 0 && (
                      <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-5">Nenhum atendimento acima do normal no período.</TableCell></TableRow>
                    )}
                    {detalhe.carregando && !(detalhe.items || []).length && (
                      <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-5">Carregando...</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <Pagination page={detalhe.page || 1} totalPages={detalhe.total_pages || 0} total={detalhe.total || 0}
                          onPage={(p) => carregarDetalhe(detalhe.item, p)} unitLabel="atendimentos" />
              <div className="flex justify-end">
                <button data-testid="ta-detalhe-fechar" onClick={() => setDetalhe(null)} className="btn-ghost h-10 px-4 text-[12px] font-medium">Fechar</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Mini filtro no padrão do Stepper da aba Anti-fraude, aceitando decimais (fatores 1,3 / 1,6). */
function StepperNum({ label, value, onChange, step = 1, testid }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full bg-neutral-50 border border-neutral-200">
      <span className="text-[9px] uppercase tracking-wider text-neutral-400">{label}</span>
      <input type="number" step={step} value={value} data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             className="h-6 w-12 bg-transparent text-center text-xs font-semibold outline-none" />
    </div>
  );
}
